// Multi-select filter dropdown chip used in the People Insights header.
//
// Visual: small pill with the dimension label + caret. Open it to get a
// popover with checkboxes, a search box, and Clear / Apply buttons at the
// bottom — same pattern as `SelectAllMultiSelect` used elsewhere in the
// app, just adapted to the chip-style trigger from the design.
//
// Two modes:
//   1. `dimension` mode (default) — values are loaded from the backend via
//      `hrStatsApi.getDimensionValues`.
//   2. `staticOptions` mode — caller supplies a fixed list (used by the
//      "Status" chip which is just ACTIVE/INACTIVE/BOTH).

import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  ButtonBase,
  Checkbox,
  CircularProgress,
  Divider,
  InputAdornment,
  List,
  ListItemButton,
  Popover,
  Stack,
  TextField,
  Typography,
  alpha,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import { useQuery } from '@tanstack/react-query';

import { hrStatsApi } from '../../../services/api';
import type {
  HrDimension,
  HrEmployeeStatus,
} from '../../../types/hrStats';
import { ACCENTS } from './peopleInsightsTokens';

export interface FilterDropdownChipStaticOption {
  value: string;
  label: string;
}

export interface FilterDropdownChipProps {
  /** Display label for the chip (e.g. "Pillar"). */
  label: string;
  /** Currently applied values (raw keys). */
  currentValues: string[];
  /** Optional human label override per value (raw key → label). */
  labelMap?: Record<string, string>;
  /** Apply a new selection. Empty array = no filter. */
  onApply: (values: string[]) => void;

  // --- Backend-driven mode ---
  dimension?: HrDimension;
  pageFilters?: Partial<Record<HrDimension, string[]>>;
  employeeStatus?: HrEmployeeStatus;

  // --- Static mode ---
  staticOptions?: FilterDropdownChipStaticOption[];
  /** Allow multi-select. Defaults to true. Set false for single-pick chips
   * (e.g. Status) where applying one value clears the others. */
  multiple?: boolean;
}

const POPOVER_WIDTH = 280;
const SEARCH_THRESHOLD = 8;

const FilterDropdownChip: React.FC<FilterDropdownChipProps> = ({
  label,
  currentValues,
  labelMap,
  onApply,
  dimension,
  pageFilters,
  employeeStatus,
  staticOptions,
  multiple = true,
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [search, setSearch] = useState('');
  // Staged selection — only committed when the user hits "Apply".
  const [stagedValues, setStagedValues] = useState<string[]>(currentValues);

  // Re-seed staged state whenever the popover opens or the upstream
  // applied values change (so chips stay consistent with URL state).
  useEffect(() => {
    setStagedValues(currentValues);
  }, [currentValues, anchorEl]);

  const open = Boolean(anchorEl);
  const accent = ACCENTS.indigo;
  const isFiltered = currentValues.length > 0;

  const handleOpen = (e: React.MouseEvent<HTMLElement>) => {
    setSearch('');
    setStagedValues(currentValues);
    setAnchorEl(e.currentTarget);
  };
  const handleClose = () => setAnchorEl(null);

  // Strip the chip's own dimension from the filter context so the option
  // list isn't narrowed by its own selection (you should be able to
  // re-pick / unpick the same dim).
  const contextFilters = useMemo(() => {
    if (!pageFilters || !dimension) return pageFilters ?? {};
    const next = { ...pageFilters };
    delete next[dimension];
    return next;
  }, [pageFilters, dimension]);

  // Lazy-load distinct values when the popover opens.
  const valuesQuery = useQuery({
    queryKey: [
      'hr-stats',
      'dimension-values',
      dimension,
      contextFilters,
      employeeStatus,
    ],
    queryFn: () =>
      hrStatsApi.getDimensionValues({
        dimension: dimension as HrDimension,
        filters: contextFilters,
        employee_status: employeeStatus,
        limit: 500,
      }),
    enabled: open && Boolean(dimension) && !staticOptions,
    staleTime: 60_000,
  });

  const allOptions: FilterDropdownChipStaticOption[] = useMemo(() => {
    if (staticOptions) return staticOptions;
    const values = valuesQuery.data?.values ?? [];
    return values.map((v) => ({ value: v, label: v }));
  }, [staticOptions, valuesQuery.data]);

  const filteredOptions = useMemo(() => {
    const s = search.trim().toLowerCase();
    if (!s) return allOptions;
    return allOptions.filter((o) => o.label.toLowerCase().includes(s));
  }, [allOptions, search]);

  const showSearch = !staticOptions && allOptions.length >= SEARCH_THRESHOLD;
  const isLoading = !staticOptions && valuesQuery.isLoading;

  const stagedSet = useMemo(() => new Set(stagedValues), [stagedValues]);
  const isDirty = useMemo(() => {
    if (stagedValues.length !== currentValues.length) return true;
    const cur = new Set(currentValues);
    return stagedValues.some((v) => !cur.has(v));
  }, [stagedValues, currentValues]);

  const toggleValue = (value: string) => {
    if (!multiple) {
      // Single-pick: applying one value replaces the rest.
      setStagedValues(stagedSet.has(value) ? [] : [value]);
      return;
    }
    setStagedValues((prev) => {
      const set = new Set(prev);
      if (set.has(value)) set.delete(value);
      else set.add(value);
      return Array.from(set);
    });
  };

  const stageSelectAll = () => {
    setStagedValues(filteredOptions.map((o) => o.value));
  };
  const stageClear = () => setStagedValues([]);

  const apply = () => {
    onApply(stagedValues);
    handleClose();
  };

  // Display logic for the chip pill:
  //   - 0 selected → "Pillar"
  //   - 1 selected → "Pillar: Enable (Care...)"
  //   - n>1        → "Pillar (3)"
  let chipText: React.ReactNode = label;
  if (currentValues.length === 1) {
    const v = currentValues[0];
    chipText = (
      <>
        {label}:{' '}
        <Box component="span" sx={{ fontWeight: 700, color: 'text.primary' }}>
          {labelMap?.[v] ?? v}
        </Box>
      </>
    );
  } else if (currentValues.length > 1) {
    chipText = (
      <>
        {label}{' '}
        <Box component="span" sx={{ fontWeight: 700, color: 'text.primary' }}>
          ({currentValues.length})
        </Box>
      </>
    );
  }

  return (
    <>
      <ButtonBase
        onClick={handleOpen}
        aria-haspopup="listbox"
        sx={{
          height: 32,
          borderRadius: 999,
          px: 1.5,
          gap: 0.5,
          fontSize: '0.8125rem',
          fontWeight: 600,
          color: isFiltered ? accent.on : 'text.primary',
          backgroundColor: isFiltered ? accent.surface : '#fff',
          border: '1px solid',
          borderColor: isFiltered ? alpha(accent.main, 0.35) : 'divider',
          transition: 'background-color 120ms ease, border-color 120ms ease',
          '&:hover': {
            backgroundColor: isFiltered ? accent.surface : '#F8FAFC',
            borderColor: isFiltered ? alpha(accent.main, 0.5) : 'text.disabled',
          },
        }}
      >
        <Box component="span" sx={{ whiteSpace: 'nowrap' }}>
          {chipText}
        </Box>
        {isFiltered ? (
          <Box
            component="span"
            role="button"
            aria-label={`Clear ${label} filter`}
            onClick={(e) => {
              e.stopPropagation();
              onApply([]);
            }}
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 16,
              height: 16,
              borderRadius: '50%',
              ml: 0.25,
              color: accent.on,
              '&:hover': { backgroundColor: alpha(accent.main, 0.15) },
            }}
          >
            <CloseIcon sx={{ fontSize: 12 }} />
          </Box>
        ) : (
          <ExpandMoreIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
        )}
      </ButtonBase>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{
          sx: {
            mt: 0.5,
            width: POPOVER_WIDTH,
            borderRadius: 2,
            boxShadow:
              '0px 8px 24px rgba(15, 23, 42, 0.08), 0px 2px 4px rgba(15, 23, 42, 0.04)',
            border: '1px solid',
            borderColor: 'divider',
            overflow: 'hidden',
          },
        }}
      >
        <Stack sx={{ maxHeight: 420 }}>
          {/* Header — title + select-all/clear shortcut row */}
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ px: 1.5, py: 1 }}
          >
            <Typography
              variant="caption"
              sx={{
                fontWeight: 700,
                color: 'text.secondary',
                textTransform: 'uppercase',
                letterSpacing: '0.06em',
              }}
            >
              {label}
            </Typography>
            {multiple && (
              <Stack direction="row" spacing={0.5}>
                <ButtonBase
                  onClick={stageSelectAll}
                  disabled={filteredOptions.length === 0}
                  sx={{
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    color: accent.on,
                    px: 0.75,
                    py: 0.25,
                    borderRadius: 1,
                    '&:hover': { backgroundColor: accent.surface },
                  }}
                >
                  Select All
                </ButtonBase>
                <ButtonBase
                  onClick={stageClear}
                  disabled={stagedValues.length === 0}
                  sx={{
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    color: 'text.secondary',
                    px: 0.75,
                    py: 0.25,
                    borderRadius: 1,
                    '&:hover': { backgroundColor: '#F1F5F9' },
                  }}
                >
                  Clear All
                </ButtonBase>
              </Stack>
            )}
          </Stack>

          {showSearch && (
            <Box sx={{ px: 1.5, pb: 1 }}>
              <TextField
                size="small"
                placeholder={`Search ${label.toLowerCase()}…`}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                fullWidth
                autoFocus
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                    </InputAdornment>
                  ),
                  sx: { fontSize: '0.8125rem', borderRadius: 1.5 },
                }}
              />
            </Box>
          )}

          <Divider />

          {/* Options list */}
          <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
            {isLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 3 }}>
                <CircularProgress size={20} />
              </Box>
            ) : filteredOptions.length === 0 ? (
              <Typography
                variant="body2"
                sx={{
                  color: 'text.secondary',
                  fontStyle: 'italic',
                  textAlign: 'center',
                  py: 3,
                }}
              >
                {search ? 'No matches' : 'No options available'}
              </Typography>
            ) : (
              <List dense sx={{ py: 0.5 }}>
                {filteredOptions.map((opt) => {
                  const selected = stagedSet.has(opt.value);
                  return (
                    <ListItemButton
                      key={opt.value}
                      onClick={() => toggleValue(opt.value)}
                      sx={{
                        py: 0.25,
                        px: 1.5,
                        '&:hover': { backgroundColor: '#F8FAFC' },
                      }}
                    >
                      <Checkbox
                        checked={selected}
                        size="small"
                        tabIndex={-1}
                        sx={{
                          mr: 0.5,
                          p: 0.5,
                          pointerEvents: 'none',
                          '& .MuiSvgIcon-root': { fontSize: 18 },
                        }}
                      />
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: selected ? 600 : 400,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          flex: 1,
                          minWidth: 0,
                        }}
                      >
                        {opt.label}
                      </Typography>
                    </ListItemButton>
                  );
                })}
              </List>
            )}
          </Box>

          {/* Footer — Apply / Clear */}
          <Divider />
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            spacing={1}
            sx={{ px: 1.5, py: 1, backgroundColor: '#F8FAFC' }}
          >
            <Button
              size="small"
              onClick={() => {
                stageClear();
                onApply([]);
                handleClose();
              }}
              disabled={!isFiltered && stagedValues.length === 0}
              sx={{
                textTransform: 'none',
                fontSize: '0.8125rem',
                color: 'text.secondary',
                '&:hover': { backgroundColor: '#F1F5F9' },
              }}
            >
              Clear
            </Button>
            <Button
              size="small"
              variant="contained"
              onClick={apply}
              disabled={!isDirty}
              sx={{
                textTransform: 'none',
                fontSize: '0.8125rem',
                backgroundColor: accent.main,
                boxShadow: 'none',
                '&:hover': { backgroundColor: accent.on, boxShadow: 'none' },
                '&.Mui-disabled': {
                  backgroundColor: alpha(accent.main, 0.4),
                  color: '#fff',
                },
              }}
            >
              Apply
            </Button>
          </Stack>
        </Stack>
      </Popover>
    </>
  );
};

export default FilterDropdownChip;
